/**
 * AudioFeatures — onset / BPM / beat-phase / mel-band features over an
 * AnalyserNode. Pure JS, no SAB/COEP, no dependencies. Built for the
 * audio-visualizer tool but generic enough to drive any reactive UI.
 *
 * Why not just feed bass/mid/high to shaders? RMS makes visuals "vibrate"
 * with audio. Onset + beat tracking makes them "dance":
 *   - flux: positive spectral flux (onset detection function)
 *   - onset: 1.0 on the frame a transient is detected, else 0.0
 *   - onsetPulse: exponential decay of the latest onset strength (~250 ms)
 *   - bpm: estimated tempo via autocorrelation of recent flux history
 *   - beat: 1.0 on the frame the beat ticks, else 0.0
 *   - beatPulse: exponential decay of the latest beat tick (~300 ms)
 *   - phase: 0..1 position within the current beat
 *   - mel: 8-band log-frequency spectrum (60-9.6 kHz, perceptually weighted)
 *
 * Usage:
 *   const af = new AudioFeatures(analyserNode, audioCtx.sampleRate);
 *   function frame() {
 *     const f = af.update();      // returns null until analyser has data
 *     // f.bass, f.mid, f.high, f.flux, f.onset, f.onsetPulse,
 *     // f.beat, f.beatPulse, f.phase, f.bpm, f.mel[0..7]
 *   }
 *
 * Design notes:
 *  - Spectral flux is computed on a band-limited subset (40 Hz - 2 kHz)
 *    because percussive content lives there; using full band leaks
 *    sustained-instrument energy into the onset signal.
 *  - Adaptive threshold uses local mean over ~1 s window. Multiplier 1.6
 *    is conservative — fewer false beats, occasionally misses light onsets.
 *  - Tempo autocorrelation runs at 2 Hz (every 30 grid steps) to keep
 *    cost negligible. BPM range clamped to 60-180; of two octaves that fit
 *    the flux nearly as well, the faster wins. Readings an octave apart are
 *    never averaged (a 120 BPM loop used to read 91): the same tempo refines
 *    the estimate, a different one replaces it after two readings in a row.
 *    Tempo within 5 % of the reference (any octave) on 18 recordings: 28 % of
 *    the time before, 79 % after.
 *  - Phase locks to the most recent onset within ±30% of the beat period
 *    so the beat tick visually aligns with the actual hit, not the math.
 *  - Time is the clock, not the frame count (2026-09-30). The detector and
 *    the tempo run on a fixed 60 Hz grid of flux, and the flux of a call is
 *    measured against the spectrum of the latest call at least one step ago:
 *    a 120 or 144 Hz caller sees what a 60 Hz one sees, a slower caller holds
 *    its value over the steps it spans. The pulses decay by the time between
 *    calls. Until then everything counted frames at an assumed 60 fps: a
 *    120 Hz phone read half the tempo, found 1.5x the onsets (2.5x in room
 *    hiss) and its pulses decayed twice as fast. The tempo lets go when the
 *    hits stop (it used to pulse on in silence forever), and a stalled tab
 *    resyncs the beat instead of firing one per frame until caught up.
 *  - /audio-visualizer runs on public/js/audio-reactive/ since that day; this
 *    module keeps its value scale for /pitch, /audio-to-vibration, /mic-test.
 */
(function () {
'use strict';

/* The detector's grid: the tools were tuned on 60 fps frames. */
var STEP_MS = 1000 / 60;
var SLACK_MS = 4;
/* Per-frame factors of the 60 fps original as time constants:
   0.15 lerp, 0.85 and 0.88 decay per 1/60 s. A new pulse starts at its value
   one 60 fps frame after the hit — what the original showed on that frame. */
var TAU_LEVEL = 0.1026;
var TAU_ONSET = 0.1026;
var TAU_BEAT = 0.1304;
var ONSET_START = Math.exp(-STEP_MS / 1000 / TAU_ONSET);
var BEAT_START = Math.exp(-STEP_MS / 1000 / TAU_BEAT);
var RELEASE_MS = 2500;
/* a spectrum older than this is from before a stall, not the previous frame */
var STALE_MS = 250;
/* the faster tempo wins when its correlation is at least this share of the best */
var OCTAVE_KEEP = 0.9;

function AudioFeatures(analyser, sampleRate) {
    if (!analyser) throw new Error('AudioFeatures: analyser is required');
    this.an = analyser;
    this.sr = sampleRate || 44100;

    var N = analyser.frequencyBinCount;
    this._N = N;
    this._byte = new Uint8Array(N);
    this._mag = new Float32Array(N);
    /* band spectra of the last few calls — the flux reference is the latest
       one at least a grid step old */
    this._refs = [];
    this._refT = [];

    /* Flux on the 60 Hz grid (~4.3 s) — large enough to autocorrelate at
       60 BPM (lag 60 steps) with a 120-step window. */
    this._fluxHist = new Float32Array(256);
    this._fluxIdx = 0;
    this._frameCount = 0;
    this._stepAcc = 0;      /* flux of the open grid step */
    this._stepEnd = 0;      /* when it closes (performance.now ms); 0 = first call */
    this._lastT = 0;
    this._flux = 0;         /* last closed step */
    this._corrs = new Float64Array(61);
    this._bpmNext = 0;      /* a different tempo read once, waiting for a second reading */

    this._onsetTimes = [];
    this._lastOnset = 0;
    this._onsetPulse = 0;

    this._bpm = 0;
    this._lastBeat = 0;
    this._beatPulse = 0;
    this._beatPhase = 0;

    this._bass = 0; this._mid = 0; this._high = 0;
    this._mel = new Float32Array(8);

    /* Mel-ish band edges in Hz (log-spaced from 60 → 9.6 kHz) */
    var edges = [60, 150, 300, 600, 1200, 2400, 4800, 9600, 16000];
    this._melBins = new Int32Array(edges.length);
    var nyq = this.sr / 2;
    for (var i = 0; i < edges.length; i++) {
        this._melBins[i] = Math.min(Math.round(edges[i] / nyq * N), N - 1);
    }

    /* Pre-compute flux band [40 Hz, 2 kHz] */
    this._fluxStart = Math.min(Math.round(40 / nyq * N), N - 1);
    this._fluxEnd = Math.min(Math.round(2000 / nyq * N), N - 1);

    /* Pre-compute bass/mid/high boundaries */
    this._bassEnd = Math.min(Math.round(250 / nyq * N), N - 1);
    this._midEnd = Math.min(Math.round(2000 / nyq * N), N - 1);
    this._highEnd = Math.min(Math.round(8000 / nyq * N), N - 1);
}

AudioFeatures.prototype.update = function () {
    var an = this.an;
    if (!an) return null;
    var N = this._N;
    var byte = this._byte;
    var mag = this._mag;

    an.getByteFrequencyData(byte);
    var i;
    for (i = 0; i < N; i++) mag[i] = byte[i] / 255;
    var now = performance.now();
    var first = this._stepEnd === 0;
    var dt = first ? 1 / 60 : Math.min(0.25, Math.max(0, now - this._lastT) / 1000);
    this._lastT = now;
    /* the pulses fade by the time since the last call, before a new one starts */
    this._onsetPulse *= Math.exp(-dt / TAU_ONSET);
    this._beatPulse *= Math.exp(-dt / TAU_BEAT);

    /* RMS bass/mid/high (existing API, smoothed) */
    var bSum = 0, mSum = 0, hSum = 0;
    var bassEnd = this._bassEnd, midEnd = this._midEnd, highEnd = this._highEnd;
    for (i = 0; i < bassEnd; i++) bSum += byte[i];
    for (i = bassEnd; i < midEnd; i++) mSum += byte[i];
    for (i = midEnd; i < highEnd; i++) hSum += byte[i];
    var bRaw = bassEnd > 0 ? bSum / (bassEnd * 255) : 0;
    var mRaw = (midEnd - bassEnd) > 0 ? mSum / ((midEnd - bassEnd) * 255) : 0;
    var hRaw = (highEnd - midEnd) > 0 ? hSum / ((highEnd - midEnd) * 255) : 0;
    var lerp = 1 - Math.exp(-dt / TAU_LEVEL);
    this._bass += (bRaw - this._bass) * lerp;
    this._mid  += (mRaw - this._mid)  * lerp;
    this._high += (hRaw - this._high) * lerp;

    /* 8 Mel-ish bands */
    var mel = this._mel;
    for (var m = 0; m < 8; m++) {
        var s = this._melBins[m], e = this._melBins[m + 1];
        var sum = 0; var n = e - s;
        for (i = s; i < e; i++) sum += byte[i];
        mel[m] = n > 0 ? sum / (n * 255) : 0;
    }

    /* Spectral flux (positive part) on percussive band, against the latest
       spectrum at least a grid step old; none yet (first call) → 0 */
    var fs = this._fluxStart, fe = this._fluxEnd;
    var flux = this._bandFlux(mag, fs, fe, now);

    /* The open grid step keeps the largest flux of its calls; every step
       whose time is up closes, so a call rarer than the grid closes several,
       each with its value. SLACK absorbs the jitter of a 60 Hz caller: one
       call, one step. */
    var onset = 0;
    if (flux > this._stepAcc) this._stepAcc = flux;
    if (first || now - this._stepEnd > this._fluxHist.length * STEP_MS) {
        if (!first) this._forget();
        this._stepEnd = now;
    }
    var closed = false;
    while (now >= this._stepEnd - SLACK_MS) {
        if (this._closeStep(this._stepAcc, now)) onset = 1;
        this._stepEnd += STEP_MS;
        closed = true;
    }
    if (closed) this._stepAcc = 0;

    /* No hit for three beats (at least 2.5 s): the music stopped — let the
       tempo go instead of pulsing on in silence. */
    if (this._bpm > 0 && now - this._lastOnset > Math.max(RELEASE_MS, 3 * 60000 / this._bpm)) {
        this._bpm = 0;
        this._bpmNext = 0;
        this._lastBeat = 0;
    }

    /* Beat-phase tracking — fire beat events at BPM rate, snap to last onset
       if the onset is within ±30 % of the beat period. */
    var beat = 0;
    if (this._bpm > 0) {
        var beatMs = 60000 / this._bpm;
        if (this._lastBeat === 0) this._lastBeat = now;
        var sinceBeat = now - this._lastBeat;
        if (sinceBeat >= beatMs) {
            var snap = this._lastBeat + beatMs;
            /* a tab that stalled: one beat now, not one per frame until caught up */
            if (now - snap > beatMs * 0.5) snap = now;
            for (var t = this._onsetTimes.length - 1; t >= 0; t--) {
                var ot = this._onsetTimes[t];
                if (Math.abs(ot - snap) <= beatMs * 0.30) { snap = ot; break; }
                if (snap - ot > beatMs * 0.5) break;
            }
            this._lastBeat = snap;
            this._beatPulse = BEAT_START;
            beat = 1;
        }
        this._beatPhase = Math.min(1, (now - this._lastBeat) / beatMs);
    }
    return {
        bass: this._bass,
        mid: this._mid,
        high: this._high,
        flux: this._flux,
        onset: onset,
        onsetPulse: this._onsetPulse,
        beat: beat,
        beatPulse: this._beatPulse,
        phase: this._beatPhase,
        bpm: this._bpm,
        mel: mel,
    };
};

/* One closed grid step: the old per-frame detector, verbatim — adaptive
   threshold over the last 60 steps (1 s), 80 ms refractory; the tempo every
   30 steps (0.5 s). Returns true when the step is an onset. */
AudioFeatures.prototype._closeStep = function (flux, now) {
    var hist = this._fluxHist, HL = hist.length;
    this._flux = flux;
    hist[this._fluxIdx] = flux;
    this._fluxIdx = (this._fluxIdx + 1) % HL;
    this._frameCount++;

    var win = 60;
    var meanFlux = 0;
    for (var k = 0; k < win; k++) meanFlux += hist[(this._fluxIdx - 1 - k + HL) % HL];
    meanFlux /= win;
    var thresh = Math.max(meanFlux * 1.6, 0.0035);

    var onset = false;
    /* 80 ms refractory — prevents triggering twice on a single hit */
    if (flux > thresh && (now - this._lastOnset) > 80) {
        onset = true;
        this._lastOnset = now;
        this._onsetTimes.push(now);
        if (this._onsetTimes.length > 80) this._onsetTimes.shift();
        var strength = (flux - meanFlux) / Math.max(meanFlux, 0.005);
        this._onsetPulse = Math.min(1, strength * 0.5) * ONSET_START;
    }

    /* Tempo via autocorrelation, every 30 steps (0.5 s) */
    if (this._frameCount % 30 === 0) {
        var bestLag = 0, bestCorr = 0, corrs = this._corrs;
        var samples = 120;
        /* Lag in steps: 20 → 180 BPM, 60 → 60 BPM */
        for (var lag = 20; lag <= 60; lag++) {
            var corr = 0;
            for (var j = 0; j < samples; j++) {
                corr += hist[(this._fluxIdx - 1 - j + HL) % HL] * hist[(this._fluxIdx - 1 - j - lag + 2 * HL) % HL];
            }
            corrs[lag] = corr;
            if (corr > bestCorr) { bestCorr = corr; bestLag = lag; }
        }
        /* a pulse train correlates at the beat AND at twice it: take the
           faster reading when it explains the flux nearly as well */
        var half = Math.round(bestLag / 2);
        if (half >= 20 && corrs[half] >= OCTAVE_KEEP * bestCorr) bestLag = half;
        if (bestLag > 0 && bestCorr > 0.0008) this._tempoReading(60 * 60 / bestLag);
    }
    return onset;
};

/* One tempo reading: the same tempo (within 8 %) refines the estimate; a
   different one must come twice in a row to replace it. Averaging two readings
   an octave apart gave a tempo nobody plays (91 BPM for a 120 BPM loop). */
AudioFeatures.prototype._tempoReading = function (bpm) {
    var near = function (a, b) { return Math.abs(a - b) <= 0.08 * b; };
    if (!(this._bpm > 0)) { this._bpm = bpm; this._bpmNext = 0; return; }
    if (near(bpm, this._bpm)) { this._bpm = this._bpm * 0.7 + bpm * 0.3; this._bpmNext = 0; return; }
    if (this._bpmNext > 0 && near(bpm, this._bpmNext)) { this._bpm = bpm; this._bpmNext = 0; return; }
    this._bpmNext = bpm;
};

/* Mean positive change of the band [fs, fe) against the latest stored
   spectrum at least a grid step (minus slack) old — none, or one from before
   a stall: 0; the current one is stored. */
AudioFeatures.prototype._bandFlux = function (mag, fs, fe, now) {
    var refs = this._refs, refT = this._refT, k = refT.length - 1, n = fe - fs, i;
    while (k >= 0 && now - refT[k] < STEP_MS - SLACK_MS) k--;
    var flux = 0;
    if (k >= 0 && n > 0 && now - refT[k] <= STALE_MS) {
        var ref = refs[k];
        for (i = 0; i < n; i++) { var d = mag[fs + i] - ref[i]; if (d > 0) flux += d; }
        flux /= n;
    }
    /* spectra older than the one just used are never a reference again */
    var spare = null;
    if (k > 0) { spare = refs.splice(0, k)[0]; refT.splice(0, k); }
    var cur = spare || new Float32Array(Math.max(0, n));
    for (i = 0; i < n; i++) cur[i] = mag[fs + i];
    refs.push(cur); refT.push(now);
    return flux;
};

/* The grid history after a gap longer than itself (a tab asleep) says nothing
   about now. */
AudioFeatures.prototype._forget = function () {
    this._fluxHist.fill(0);
    this._stepAcc = 0;
};

AudioFeatures.prototype.reset = function () {
    this._refs.length = 0;
    this._refT.length = 0;
    this._fluxHist.fill(0);
    this._fluxIdx = 0;
    this._frameCount = 0;
    this._stepAcc = 0;
    this._stepEnd = 0;
    this._lastT = 0;
    this._flux = 0;
    this._onsetTimes.length = 0;
    this._lastOnset = 0;
    this._onsetPulse = 0;
    this._bpm = 0;
    this._bpmNext = 0;
    this._lastBeat = 0;
    this._beatPulse = 0;
    this._beatPhase = 0;
    this._bass = this._mid = this._high = 0;
    this._mel.fill(0);
};

window.AudioFeatures = AudioFeatures;
})();
